import { memo, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Check, Clock, Lock, ShoppingBasket } from 'lucide-react';
import type { RecipeSummary } from '@dishly/shared';
import { cx } from '../lib/cx';
import { useFormat } from '../lib/format';
import { putListItem, removeListItem } from '../lib/lists';
import { useRequireLogin } from '../lib/navigation';
import { recipePath } from '../lib/recipes';
import { useListOfKind } from '../stores/data';
import { toast } from '../stores/toasts';
import { Badge } from './common';
import { RecipeImage } from './RecipeImage';
import { RatingBadge } from './Stars';

/** Adds the recipe's ingredients to the shopping list, or removes them again. */
export function CartButton({
  recipe,
  servings,
  variant = 'icon',
}: {
  recipe: Pick<RecipeSummary, 'id' | 'name' | 'servings'>;
  servings?: number;
  variant?: 'icon' | 'full';
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const requireLogin = useRequireLogin();
  const shopping = useListOfKind('shopping');
  const inList = !!shopping?.items.some((item) => item.recipeId === recipe.id);
  const label = inList ? t('cart.remove') : t('cart.add');

  const onClick = async (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!requireLogin() || !shopping) return;
    if (inList) {
      await removeListItem(shopping.id, recipe.id);
      toast(t('cart.removed'));
    } else {
      const scaled = servings && servings !== recipe.servings ? servings : null;
      await putListItem(shopping.id, recipe.id, { servings: scaled, collapsed: false });
      toast(t('cart.added'), {
        tone: 'success',
        action: { label: t('cart.view'), onClick: () => void navigate('/lists/shopping') },
      });
    }
  };

  if (variant === 'full') {
    return (
      <button type="button" onClick={onClick} className={inList ? 'btn-secondary' : 'btn-primary'} aria-pressed={inList}>
        {inList ? <Check className="size-4" /> : <ShoppingBasket className="size-4" />}
        {label}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={inList}
      aria-label={label}
      title={label}
      className={cx(
        'relative z-10 inline-flex size-10 shrink-0 items-center justify-center rounded-full border transition-colors',
        inList ? 'border-herb bg-herb text-white' : 'border-line bg-surface text-ink hover:border-brand hover:text-brand',
      )}
    >
      {inList ? <Check className="size-5" /> : <ShoppingBasket className="size-5" />}
    </button>
  );
}

function StatusBadges({ recipe }: { recipe: RecipeSummary }) {
  const { t } = useTranslation();
  if (recipe.isDraft) return <Badge tone="brand">{t('badge.draft')}</Badge>;
  if (recipe.visibility === 'private') {
    return (
      <Badge>
        <Lock className="size-3" /> {t('badge.private')}
      </Badge>
    );
  }
  return null;
}

/** Grid card: mostly picture, then name, rating and the shopping-list button. */
export const RecipeCard = memo(function RecipeCard({ recipe }: { recipe: RecipeSummary }) {
  const { t } = useTranslation();
  const name = recipe.name || t('myRecipes.untitled');
  return (
    <article className="group relative flex flex-col overflow-hidden rounded-3xl border border-line bg-surface transition-shadow hover:shadow-lg">
      <div className="aspect-[4/3] overflow-hidden bg-surface-2">
        <RecipeImage
          image={recipe.cover}
          alt={name}
          sizes="(min-width: 1280px) 300px, (min-width: 640px) 45vw, 100vw"
          className="size-full transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 items-start gap-3 p-4">
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 font-display text-lg leading-snug font-semibold">
            <Link to={recipePath(recipe)} className="after:absolute after:inset-0 focus-visible:outline-none">
              {name}
            </Link>
          </h3>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <RatingBadge avg={recipe.ratingAvg} count={recipe.ratingCount} />
            <StatusBadges recipe={recipe} />
          </div>
        </div>
        {!recipe.isDraft && <CartButton recipe={recipe} />}
      </div>
    </article>
  );
});

/** List row: thumbnail plus more detail (time, tags, author). */
export const RecipeRow = memo(function RecipeRow({ recipe, showAuthor = true }: { recipe: RecipeSummary; showAuthor?: boolean }) {
  const { t } = useTranslation();
  const format = useFormat();
  const name = recipe.name || t('myRecipes.untitled');
  return (
    <article className="relative flex items-center gap-4 rounded-2xl border border-line bg-surface p-3 transition-shadow hover:shadow-md">
      <div className="size-20 shrink-0 overflow-hidden rounded-xl bg-surface-2 sm:size-24">
        <RecipeImage image={recipe.cover} alt={name} sizes="96px" className="size-full" />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="truncate font-display text-lg font-semibold">
          <Link to={recipePath(recipe)} className="after:absolute after:inset-0 focus-visible:outline-none">
            {name}
          </Link>
        </h3>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted">
          <RatingBadge avg={recipe.ratingAvg} count={recipe.ratingCount} className="text-ink" />
          {recipe.totalTimeMinutes && (
            <span className="inline-flex items-center gap-1">
              <Clock className="size-4" /> {format.duration(recipe.totalTimeMinutes)}
            </span>
          )}
          {showAuthor && <span className="truncate">{t('recipe.by', { name: recipe.author.displayName })}</span>}
          <StatusBadges recipe={recipe} />
        </div>
        {recipe.tags.length > 0 && (
          <div className="mt-2 hidden flex-wrap gap-1.5 sm:flex">
            {recipe.tags.map((tag) => (
              <Badge key={tag}>{t(`tags.${tag}`)}</Badge>
            ))}
          </div>
        )}
      </div>
      {!recipe.isDraft && <CartButton recipe={recipe} />}
    </article>
  );
});
